import { Sidebar } from "primereact/sidebar";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DatabaseIcon from "../../../../components/icons/DatabaseIcon";
import DisconnectIcon from "../../../../components/icons/DisconnectIcon";
import HomeIcon from "../../../../components/icons/HomeIcon";
import JournalsIcon from "../../../../components/icons/JournalsIcon";
import SettingsIcon from "../../../../components/icons/SettingsIcon";
import StatisticsIcon from "../../../../components/icons/StatisticsIcon";
import SupportIcon from "../../../../components/icons/SupportIcon";
import { useAppStore } from "../../../../store/app.store";
import { useAuthenticatedUser } from "../../../../store/store-hooks";
import GovLogo from "../../../assets/images/gov-logo-sidebar.png";
import { syncOpenModalDialog } from "../../../shared/global-tools/dialog-system";
import { forgetSession } from "../../../shared/global-tools/session-storage";
import makeConfirmDialog from "../../utils/ConfirmDialog/ConfirmDialog";

import { showToast } from "../../../shared/global-tools/toast-system";
import Icon from "../../utils/Icons/Icon";
import "./AppSidebar.scss";

export default function AppSidebar({
  visible,
  setVisible,
  asDrawer,
}: {
  visible: boolean;
  setVisible: (value: boolean) => void;
  asDrawer?: boolean;
}) {
  const isDesktopMode = window.matchMedia("(min-width: 992px)").matches;
  const onRouteChange = isDesktopMode ? undefined : () => setVisible(false);
  const { pathname } = useLocation();
  const setAuthenticated = useAppStore((state) => state.setIsAuthenticated);
  const appLayout = useAppStore((state) => state.appLayout);
  const authUser = useAuthenticatedUser();
  const navigate = useNavigate();

  const disconnect = () => {
    forgetSession();
    setAuthenticated(false);
    navigate("/");
    showToast("Déconnexion réussie", { severity: "success" });
  };

  useEffect(() => {
    if (appLayout === "mobile") setVisible(false);
  }, [location]);

  return (
    <Sidebar
      closeOnEscape={asDrawer}
      visible={visible}
      className="AppSidebar"
      modal={asDrawer}
      dismissable={asDrawer}
      onHide={() => setVisible(false)}
      showCloseIcon={asDrawer}
      header={
        <div className="AppSidebar-header">
          {/* TODO: remove */}
          <img
            src={authUser.organization?.logo ? authUser.organization?.logo : GovLogo}
            width="279"
            height="90"
            alt=""
          />
          {/* <img src={GovLogo} width="279" height="90" alt="" /> */}
        </div>
      }
    >
      <div className="AppSidebar-content">
        <ul className="AppSidebar-menu">
          <li>
            <SidebarMenuItem
              label="Tableau de bord"
              navigateTo="/app"
              icon={<HomeIcon classNames="AppSidebar-menuItemIcon" />}
              onRouteChange={onRouteChange}
            />
          </li>
          <li>
            <SidebarDropdown
              active={pathname.split("/")[2] === "databases"}
              label="Référentiels"
              icon={<DatabaseIcon classNames="AppSidebar-menuItemIcon" />}
              onRouteChange={onRouteChange}
              subMenuItems={[
                {
                  label: "Créer un référentiel",
                  route: "/app/databases/create",
                },
                {
                  label: "Liste des référentiels",
                  route: "/app/databases",
                },
                {
                  label: "Import en masse",
                  route: "/app/databases/import",
                },
              ]}
            />
          </li>

          <li>
            <SidebarMenuItem
              label="Structures"
              navigateTo="/app/organizations"
              icon={<Icon name="organization" className="AppSidebar-menuItemIcon" />}
              onRouteChange={onRouteChange}
            />
          </li>
          <li>
            <SidebarMenuItem
              label="Journals"
              navigateTo="/app/journals"
              icon={<JournalsIcon classNames="AppSidebar-menuItemIcon" />}
              onRouteChange={onRouteChange}
            />
          </li>

          <li>
            <SidebarMenuItem
              label="Statistiques"
              navigateTo="/app/statistics"
              icon={<StatisticsIcon classNames="AppSidebar-menuItemIcon" />}
              onRouteChange={onRouteChange}
            />
          </li>

          <li>
            <SidebarDropdown
              active={pathname.includes("settings")}
              label="Configuration"
              icon={<SettingsIcon classNames="AppSidebar-menuItemIcon" />}
              onRouteChange={onRouteChange}
              subMenuItems={[
                {
                  label: "Gestion des Utilisateurs",
                  route: "/app/settings/users",
                },
                {
                  label: "Paramétrages des KPI",
                  route: "/app/settings",
                },
                {
                  label: "Développeur",
                  route: "/app/settings/developer",
                },
              ]}
            />
          </li>

          <li>
            <SidebarMenuItem
              label="Support"
              navigateTo="/app/support"
              icon={<SupportIcon classNames="AppSidebar-menuItemIcon" />}
              onRouteChange={onRouteChange}
            />
          </li>

          <li>
            <button
              className="AppSidebar-menuItem danger"
              type="button"
              onClick={() =>
                syncOpenModalDialog(makeConfirmDialog, {
                  onConfirm: disconnect,
                  warningMessage: "Voulez-vous vraiment vous déconnecter?",
                  header: "Confirmation",
                  label: "Se déconnecter",
                })
              }
            >
              <DisconnectIcon classNames="AppSidebar-menuItemIcon" />
              Se déconnecter
            </button>
          </li>
        </ul>
      </div>
    </Sidebar>
  );
}

function SidebarMenuItem({
  navigateTo,
  icon,
  label,
  onRouteChange,
}: { label: string; navigateTo: string; icon: JSX.Element; onRouteChange?(): void }) {
  const { pathname } = useLocation();
  const linkActive = navigateTo === "/app" ? pathname === navigateTo : pathname.startsWith(navigateTo);

  return (
    <Link
      to={navigateTo}
      onClick={onRouteChange}
      className={`AppSidebar-menuItem ${linkActive ? "active" : ""}`}
    >
      {icon}
      <span>{label}</span>
    </Link>
  );
}

interface SubMenuItem {
  route: string;
  label: string;
}

function SidebarDropdown({
  label,
  active,
  subMenuItems,
  icon,
  onRouteChange,
}: {
  label: string;
  active: boolean;
  subMenuItems: SubMenuItem[];
  icon: JSX.Element;
  onRouteChange?(): void;
}) {
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!active) {
      setShow(false);
    }
  }, [pathname]);

  return (
    <>
      <button
        type="button"
        className={`AppSidebar-menuItem ${active || show ? "active" : ""} `}
        onClick={() => setShow(!show)}
      >
        {icon}
        {label}
      </button>
      <ul className={`AppSidebar-subMenu ${show ? "visible" : ""}`}>
        {subMenuItems.map((item) => {
          const isExactMatch = pathname === item.route;
          const isNestedMatch = pathname.startsWith(`${item.route}/`) &&
            !subMenuItems.some(otherItem =>
              otherItem.route !== item.route &&
              pathname === otherItem.route
            );
          const isActive = isExactMatch || isNestedMatch;
          return (
            <li key={item.route}>
              <Link
                to={item.route}
                onClick={onRouteChange}
                className={`AppSidebar-subMenuItem ${isActive ? "active" : ""}`}
              >
                {item.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </>
  );
}
